import i18n from 'i18next'

import { ProductType } from '../../types'
import { getStringDuration } from '../../utils/getStringExecutionDuration'

export const getResolutionsErrorMessage = (
  height: number,
  width: number,
  maxAllowedResolution: number,
  productType: ProductType,
  t: (arg0: string, arg1?: object) => string
): string => {
  const MIN_RES = 64
  const MAX_RES = 1024
  // eslint-disable-next-line react-hooks/rules-of-hooks

  if (!(height >= MIN_RES && height <= MAX_RES)) {
    return t(
      'Allowed height value between {{ minResolution }} and {{ maxResolution }}',
      { minResolution: MIN_RES, maxResolution: MAX_RES }
    )
  }
  if (!(width >= MIN_RES && width <= MAX_RES)) {
    return t(
      'Allowed width value between {{ minResolution }} and {{ maxResolution }}',
      { minResolution: MIN_RES, maxResolution: MAX_RES }
    )
  }
  if (!(width <= maxAllowedResolution / height)) {
    let w = Math.round(maxAllowedResolution / height)
    if (productType === 'segmentation') {
      if (w % 32 !== 0) {
        w -= w % 32
      }
    }
    return t(
      'Width value not compatible with height value, Height*Width exceed {{ maxResolution }}, use {{ maxWidth }} or lower',
      { maxWidth: w, maxResolution: maxAllowedResolution }
    )
  }
  if (!(height * width <= maxAllowedResolution)) {
    return t(
      'Resolution out of range, Height*Width must be equal or lower than {{ maxResolution }}',
      { maxResolution: maxAllowedResolution }
    )
  }
  if (productType === 'segmentation') {
    if (height % 32 !== 0) {
      return t('Height must be multiple of 32')
    }
    if (width % 32 !== 0) {
      return t('Width must be multiple of 32')
    }
  }

  return ''
}

interface IParameterTrainingTime {
  productType: ProductType
  backbone: string
  strategy?: string
  resolution?: {
    height: number
    width: number
  }
  precision?: 'fp32' | 'fp16'
  a: number
  b: number
  c: number
  d: number
}

const parametersTrainingTime: IParameterTrainingTime[] = [
  {
    productType: 'anomaly',
    strategy: 'advanced',
    backbone: 'resnet18.tv_in1k', // lightweight
    precision: 'fp16',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    a: parseFloat((0.01611748237294756).toFixed(4)),
    b: 5,
    c: parseFloat((1.9960932151041777).toFixed(4)),
    d: 360,
  }, // 1
  {
    productType: 'anomaly',
    strategy: 'advanced',
    backbone: 'resnet18.tv_in1k', // lightweight
    precision: 'fp16',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    a: parseFloat((0.02760450323153046).toFixed(4)),
    b: 5,
    c: parseFloat((1.540443879896925).toFixed(4)),
    d: 360,
  }, // 2
  {
    productType: 'anomaly',
    strategy: 'advanced',
    backbone: 'resnet18.tv_in1k', // lightweight
    precision: 'fp32',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    a: parseFloat((0.017411919534350485).toFixed(4)),
    b: 5,
    c: parseFloat((1.9960471912178073).toFixed(4)),
    d: 360,
  }, // 3
  {
    productType: 'anomaly',
    strategy: 'advanced',
    backbone: 'resnet18.tv_in1k', // lightweight
    precision: 'fp32',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    a: parseFloat((0.03540879824148072).toFixed(4)),
    b: 5,
    c: parseFloat((1.5407821505515042).toFixed(4)),
    d: 360,
  }, // 4
  {
    productType: 'anomaly',
    strategy: 'advanced',
    backbone: 'wide_resnet50_2.tv_in1k', // heavy
    precision: 'fp16',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    a: parseFloat((0.010785491791210298).toFixed(4)),
    b: 5,
    c: parseFloat((2.099913503615052).toFixed(4)),
    d: 360,
  }, // 5
  {
    productType: 'anomaly',
    strategy: 'advanced',
    backbone: 'wide_resnet50_2.tv_in1k', // heavy
    precision: 'fp16',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    a: parseFloat((0.0008424369434371258).toFixed(4)),
    b: 5,
    c: parseFloat((2.221654765772755).toFixed(4)),
    d: 360,
  }, // 6
  {
    productType: 'anomaly',
    strategy: 'advanced',
    backbone: 'wide_resnet50_2.tv_in1k', // heavy
    precision: 'fp32',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    a: parseFloat((0.017521651005186486).toFixed(4)),
    b: 5,
    c: parseFloat((2.099951621087707).toFixed(4)),
    d: 360,
  }, // 7
  {
    productType: 'anomaly',
    strategy: 'advanced',
    backbone: 'wide_resnet50_2.tv_in1k', // heavy
    precision: 'fp32',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    a: parseFloat((0.0014717332602531383).toFixed(4)),
    b: 5,
    c: parseFloat((2.2219009578090043).toFixed(4)),
    d: 360,
  }, // 8
  {
    productType: 'anomaly',
    strategy: 'simple',
    backbone: 'resnet18.tv_in1k', // lightweight
    precision: 'fp16',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    a: parseFloat((0.6205150940974421).toFixed(4)),
    b: 5,
    c: parseFloat((0.9853413172428286).toFixed(4)),
    d: 360,
  }, // 9
  {
    productType: 'anomaly',
    strategy: 'simple',
    backbone: 'resnet18.tv_in1k', // lightweight
    precision: 'fp16',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    a: parseFloat((0.1861979627871401).toFixed(4)),
    b: 5,
    c: parseFloat((1.1162990717907895).toFixed(4)),
    d: 360,
  }, // 10
  {
    productType: 'anomaly',
    strategy: 'simple',
    backbone: 'resnet18.tv_in1k', // lightweight
    precision: 'fp32',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    a: parseFloat((0.682233259173534).toFixed(4)),
    b: 5,
    c: parseFloat((0.9867155238518627).toFixed(4)),
    d: 360,
  }, // 11
  {
    productType: 'anomaly',
    strategy: 'simple',
    backbone: 'resnet18.tv_in1k', // lightweight
    precision: 'fp32',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    a: parseFloat((0.19309274878909025).toFixed(4)),
    b: 5,
    c: parseFloat((1.1179421655222554).toFixed(4)),
    d: 360,
  }, // 12
  {
    productType: 'anomaly',
    backbone: 'logical_anomaly',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    precision: 'fp16',
    a: parseFloat((0.0).toFixed(4)),
    b: 5,
    c: parseFloat((1.0).toFixed(4)),
    d: 12600,
  }, // 13
  {
    productType: 'anomaly',
    backbone: 'logical_anomaly',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    precision: 'fp32',
    a: parseFloat((0.0).toFixed(4)),
    b: 5,
    c: parseFloat((1.0).toFixed(4)),
    d: 4500,
  }, // 14
  {
    productType: 'anomaly',
    backbone: 'logical_anomaly',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    precision: 'fp16',
    a: parseFloat((0.0).toFixed(4)),
    b: 5,
    c: parseFloat((1.0).toFixed(4)),
    d: 11760,
  }, // 15
  {
    productType: 'anomaly',
    backbone: 'logical_anomaly',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    precision: 'fp32',
    a: parseFloat((0.0).toFixed(4)),
    b: 5,
    c: parseFloat((1.0).toFixed(4)),
    d: 5040,
  }, // 16
  {
    productType: 'classification',
    backbone: 'fine_tuning',
    precision: 'fp16',
    a: parseFloat((2.116986232981397).toFixed(4)),
    b: 5,
    c: parseFloat((0.9766917318397556).toFixed(4)),
    d: 360,
  }, // 17
  {
    productType: 'classification',
    backbone: 'fine_tuning',
    precision: 'fp32',
    a: parseFloat((2.2345683230092064).toFixed(4)),
    b: 5,
    c: parseFloat((0.9767808491224451).toFixed(4)),
    d: 360,
  }, // 18
  {
    productType: 'classification',
    backbone: 'dino_vits8_runtime', // big
    precision: 'fp16',
    a: parseFloat((4.630671818407256).toFixed(4)),
    b: 5,
    c: parseFloat((0.4727762231203991).toFixed(4)),
    d: 360,
  }, // 19
  {
    productType: 'classification',
    backbone: 'dino_vits8_runtime', // big
    precision: 'fp32',
    a: parseFloat((6.946027799073133).toFixed(4)),
    b: 5,
    c: parseFloat((0.4727758953791271).toFixed(4)),
    d: 360,
  }, // 20
  {
    productType: 'classification',
    backbone: 'dino_vitb8_runtime', // huge
    precision: 'fp16',
    a: parseFloat((2.048358044388374).toFixed(4)),
    b: 5,
    c: parseFloat((0.6083826307506497).toFixed(4)),
    d: 360,
  }, // 21
  {
    productType: 'classification',
    backbone: 'dino_vitb8_runtime', // huge
    precision: 'fp32',
    a: parseFloat((8.891331385175775).toFixed(4)),
    b: 5,
    c: parseFloat((0.6089521920578151).toFixed(4)),
    d: 360,
  }, // 22
  {
    productType: 'classification',
    backbone: 'vit_tiny16_runtime', // tiny
    precision: 'fp16',
    a: parseFloat((0.545581265196031).toFixed(4)),
    b: 5,
    c: parseFloat((0.737891786897521).toFixed(4)),
    d: 360,
  }, // 23
  {
    productType: 'classification',
    backbone: 'vit_tiny16_runtime', // tiny
    precision: 'fp32',
    a: parseFloat((0.36492524403166227).toFixed(4)),
    b: 5,
    c: parseFloat((0.7401828318387085).toFixed(4)),
    d: 360,
  }, // 24
  {
    productType: 'segmentation',
    backbone: 'mobilenet_v2_unet', // standard
    precision: 'fp16',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    a: parseFloat((10.773219867948038).toFixed(4)),
    b: 5,
    c: parseFloat((0.8904459697588332).toFixed(4)),
    d: 360,
  }, // 25
  {
    productType: 'segmentation',
    backbone: 'mobilenet_v2_unet', // standard
    precision: 'fp16',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    a: parseFloat((45.79358655680453).toFixed(4)),
    b: 5,
    c: parseFloat((0.820838194000418).toFixed(4)),
    d: 360,
  }, // 26
  {
    productType: 'segmentation',
    backbone: 'mobilenet_v2_unet', // standard
    precision: 'fp32',
    resolution: {
      height: 448,
      width: 448,
    }, // high
    a: parseFloat((17.506421887169452).toFixed(4)),
    b: 5,
    c: parseFloat((0.8904450020854212).toFixed(4)),
    d: 360,
  }, // 27
  {
    productType: 'segmentation',
    backbone: 'mobilenet_v2_unet', // standard
    precision: 'fp32',
    resolution: {
      height: 224,
      width: 224,
    }, // standard
    a: parseFloat((25.88183810655531).toFixed(4)),
    b: 5,
    c: parseFloat((0.820841408763799).toFixed(4)),
    d: 360,
  }, // 28
  {
    productType: 'classification_patch',
    backbone: 'vit_tiny16_runtime', // tiny
    a: 0,
    b: 5,
    c: parseFloat((1.0000000000000002).toFixed(4)),
    d: 360,
  },
  {
    productType: 'classification_patch',
    backbone: 'dino_vits8_runtime', // big
    a: parseFloat((0.6408402158115988).toFixed(4)),
    b: 5,
    c: parseFloat((1.0637846003178073).toFixed(4)),
    d: 360,
  },
  {
    productType: 'classification_patch',
    backbone: 'dino_vitb8_runtime', // huge
    a: parseFloat((2.3392274224085825).toFixed(4)),
    b: 5,
    c: parseFloat((0.9916497209317184).toFixed(4)),
    d: 360,
  },
]

const getParameterTrainingTime = (
  productType: ProductType,
  backbone: string,
  strategy?: string,
  resolution?: {
    height: number
    width: number
  },
  precision?: string,
  backboneIsLogicalAnomaly?: boolean
): IParameterTrainingTime | undefined =>
  parametersTrainingTime.find(
    (p) =>
      p.productType === productType &&
      ((productType === 'anomaly' &&
        !backboneIsLogicalAnomaly &&
        p.backbone === backbone) ||
        p.backbone === backbone) &&
      (strategy === undefined ||
        (productType === 'anomaly' && backboneIsLogicalAnomaly) ||
        p.strategy === strategy?.toLowerCase()) &&
      (resolution === undefined ||
        (p.resolution?.height === resolution?.height &&
          p.resolution?.width === resolution?.width)) &&
      (precision === undefined || p.precision === precision)
  )

const calculateTrainingDuration = (
  numOfImages: number,
  a: number,
  b: number,
  c: number,
  d: number
): number => a * (numOfImages - b) ** c + d + 6 * 60 // 6 min for provisioning

interface IGetEstimatedTrainingDurationProps {
  productType: ProductType
  numOfImages: number
  backbones?: {
    code: string
    label: string
  }[]
  strategy?: string
  resolution?: {
    height: number
    width: number
  }
  precision?: string
}

export interface ITrainingDuration {
  sec: number
  backbone?: string
}

export const getEstimatedTrainingsDuration = ({
  productType,
  numOfImages,
  backbones,
  strategy,
  resolution,
  precision,
}: IGetEstimatedTrainingDurationProps): ITrainingDuration[] => {
  const seconds: ITrainingDuration[] = []
  let param: IParameterTrainingTime | undefined
  let resolutionIsCustom = false

  // manage custom resolution (when resolution is not 224*224 or 448*448)
  if (
    resolution &&
    !(
      (resolution.height === 224 && resolution.width === 224) ||
      (resolution.height === 448 && resolution.width === 448)
    )
  ) {
    resolutionIsCustom = true
  }

  backbones?.forEach((backbone) => {
    // if backbone is "logical anomaly" then don't consider strategy
    // if backbone is not "logical anomaly" then don't consider backbone
    let backboneIsLogicalAnomaly = false
    if (
      backbone &&
      backbone.code === 'logical_anomaly' &&
      productType === 'anomaly'
    ) {
      backboneIsLogicalAnomaly = true
    }

    // Custom resolution
    if (resolutionIsCustom) {
      let secondsHighResolution = 0
      let secondsStandardResolution = 0
      const paramHighResolution = getParameterTrainingTime(
        productType,
        backbone.code,
        strategy,
        {
          height: 448,
          width: 448,
        },
        precision,
        backboneIsLogicalAnomaly
      )
      if (paramHighResolution) {
        secondsHighResolution = calculateTrainingDuration(
          numOfImages,
          paramHighResolution.a,
          paramHighResolution.b,
          paramHighResolution.c,
          paramHighResolution.d
        )
      }
      const paramStandardResolution = getParameterTrainingTime(
        productType,
        backbone.code,
        strategy,
        {
          height: 224,
          width: 224,
        },
        precision,
        backboneIsLogicalAnomaly
      )
      if (paramStandardResolution) {
        secondsStandardResolution = calculateTrainingDuration(
          numOfImages,
          paramStandardResolution.a,
          paramStandardResolution.b,
          paramStandardResolution.c,
          paramStandardResolution.d
        )
      }
      const pixelHighResolution = 448 * 448
      const pixelStandardResolution = 224 * 224
      const pixelCustomResolution = resolution!.height * resolution!.width
      seconds.push({
        sec:
          ((secondsStandardResolution - secondsHighResolution) /
            (pixelStandardResolution - pixelHighResolution)) *
            pixelCustomResolution +
          (pixelStandardResolution * secondsHighResolution -
            secondsStandardResolution * pixelHighResolution) /
            (pixelStandardResolution - pixelHighResolution),
        backbone: backbone && backbone.label.split('[')[0].trim(),
      })
    } else {
      // High or standard resolution
      param = getParameterTrainingTime(
        productType,
        backbone.code,
        strategy,
        resolution,
        precision,
        backboneIsLogicalAnomaly
      )
      if (param) {
        seconds.push({
          sec: calculateTrainingDuration(
            numOfImages,
            param.a,
            param.b,
            param.c,
            param.d
          ),
          backbone: backbone && backbone.label.split('[')[0].trim(),
        })
      }
    }
  })

  return seconds
}

export const formatEstimatedTrainingsDuration = (
  estimatedTrainingsDuration: ITrainingDuration[]
): string => {
  if (estimatedTrainingsDuration.length >= 1) {
    return `${i18n.t(
      'Estimated minimum trainings duration'
    )}: ${estimatedTrainingsDuration
      .map((s) => `${getStringDuration(s.sec)} (${s.backbone})`)
      .join(', ')}.`
  }

  return i18n.t('Impossible to estimate the training duration.')
}
